import {
  Component,
  AfterViewChecked,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Location } from "@angular/common";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { cloneDeep } from "lodash";
import { Observable, Subscription, filter } from "rxjs";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";

import { openClose } from "src/app/animations";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { EventService } from "src/app/core/core-services/services/event.service";
import { trackService } from "src/app/core/core-services/services/track.service";
import { MenuService } from "./menu.service";
import { NotifyService } from "./not.service";
import {
  ShortcutCategoryService,
  SHORTCUT_CATEGORIES,
} from "src/app/core/core-services/services/shortcut-category.service";

import {
  FLYSHARE_ALL_ROUTE,
  FLYSHARE_OTHERS_ROUTE,
  FLYSHARE_SHARED_ROUTE,
} from "src/app/vengreso/share/flyshare/flyshare.config";
import { AdminLayoutMenuService } from "../admin-layout/admin-layout-menu.service";
import { AuthService } from "../core-services/services/auth.service";
import { LoadingService } from "../core-services/services/loader.service";
import { NavigationService } from "../core-services/services/navigation.service";
import { SsoRedirectService } from "../core-services/services/sso-redirect.service";
import { DialogService } from "../core-services/services/dialog.service";
import { SharedDataService } from "../core-services/services/shared-data.service";
import { DynamicStorage } from "../core-services/decorators/dynamic-storage";
import { GlobalHookService } from "src/app/global-hook.service";
import { VngTourService } from "@vengreso/angular-components";
import { selectPlanMenuDisplayName, selectPlanIdentifier, selectHasFlyLearning } from "../store/plan/plan.selectors";
import { tagTourStepButtons } from "src/app/utils/tour-heap.util";
import { normalizeAvatarUrl } from "src/app/utils/avatar.util";

@Component({
    selector: "app-menu",
    animations: [openClose],
    templateUrl: "./menu.component.html",
    styleUrls: ["./menu.component.scss"],
    standalone: false
})
export class MenuComponent implements AfterViewChecked, OnChanges, OnInit, OnDestroy {
  @Input() direction: string;
  @DynamicStorage(SHORTCUT_CATEGORIES) shortcutCategoryList: any[];
  currentLang = "en";
  menuItems = [];
  submenu: any;
  planId$: Observable<string>;
  user: any;

  /**
   * fix(M9): the avatar `<img>` had no `(error)` handler, so a 404/403 avatar
   * left a broken-image glyph in the side menu for the rest of the session.
   * Flip to the same `person` icon used when the user has no avatar at all.
   */
  avatarFailed = false;

  /** Route-independent avatar URL — see `normalizeAvatarUrl`. */
  get avatarSrc(): string {
    return normalizeAvatarUrl(this.user?.avatar);
  }

  viewAll = {
    id: "all",
    name: "View All",
  };

  config = {
    classname: "custom-icon",
    paddingAtStart: false,
    interfaceWithRoute: true,
    collapseOnSelect: true,
    highlightOnSelect: true,
    rtlLayout: false,
    selectedListFontColor: "#3f51b5",
  };

  subscriptions = new Subscription();
  userPlan: string;

  flyLearningShow = false;
  /** Cached snapshot of `plans.has_fly_learning` from the NgRx plan store. */
  hasFlyLearning = false;
  notifierSubscription: Subscription = this.notifyService.subjectNotifier.subscribe((notified) => {
    if (notified && notified.plan) {
      this.userPlan = notified.plan.toLowerCase();
    }

    if (notified && notified.planObj) {
      this.userPlan = notified.planObj?.identifier;
    }
    this.user = this.authService.userDetails.user_details;
    this.getShortcutCategoryList(true);
    this.getTemplateCategory();
  });

  @Output() closeSideNav = new EventEmitter();
  @ViewChild("menuHeight") menuHeight: ElementRef;
  selectedMenuIndex: number | null = null;

  constructor(
    public dialog: DialogService,
    private readonly router: Router,
    public menuService: MenuService,
    public notifyService: NotifyService,
    private readonly dataService: DataService,
    private readonly eventService: EventService,
    public trackService: trackService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly adminLayoutMenuService: AdminLayoutMenuService,
    private readonly loaderService: LoadingService,
    private readonly location: Location,
    private readonly shortcutCategoryService: ShortcutCategoryService,
    private readonly sharedData: SharedDataService,
    config: NgbPopoverConfig,
    private readonly globalHookService: GlobalHookService,
    private readonly tourService: VngTourService,
    private readonly ssoRedirect: SsoRedirectService,
    private readonly navigationService: NavigationService,
    private readonly store: Store
  ) {
    this.user = this.authService.userDetails.user_details;
    this.menuItems = this.menuService.getAll();

    this.submenu = {
      FlyCuts: [],
      FlyPlates: [],
      FlyShare: [],
    };

    if (this.authService.showAdminSection() || this.authService.showVengresoAdminSection()) {
      this.submenu.Administration = [{ id: "settings", name: "Personal Settings" }];

      if (this.authService.showAdminSection()) {
        this.submenu.Administration.push({
          id: this.menuItems.find((menu) => menu.label == "Administration" || menu.label == "Administration")
            .sublink_two,
          name: "Account Center",
        });
      }

      if (this.authService.showVengresoAdminSection()) {
        this.submenu.Administration.push({
          id: this.menuItems.find((menu) => menu.label == "Administration" || menu.label == "Administration")
            .sublink_three,
          name: "Client Management Center",
        });
      }
    }

    // Initialize plan observable from NgRx store
    this.planId$ = this.store.select(selectPlanMenuDisplayName);

    // Subscribe to the authoritative FlyLearning entitlement flag
    // (`plans.has_fly_learning`) — both the menu icon visibility and the
    // click-time gate must read the same source of truth so operators in
    // CMC can toggle access without code changes.
    this.subscriptions.add(
      this.store.select(selectHasFlyLearning).subscribe((hasFlyLearning) => {
        this.hasFlyLearning = hasFlyLearning;
        this.flyLearningShow = hasFlyLearning;
      })
    );

    this.subscriptions.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((route: any) => {
        if (route.url.includes("instancyssorequested=true")) {
          this.loaderService.start();
          this.location.replaceState(`flyboard`);
          this.openlearning(this.menuItems.find((item) => item.sso === "flylearning"));
          this.loaderService.stop();
        }

        if (route.url.includes("/flyshare")) {
          let flyshareIndex = this.menuItems.findIndex((item) => item.sublink === "/flyshare");
          this.menuItems[flyshareIndex].isOpen = true;
        }
        if (route.url.includes("/shortcuts")) {
          let shortcutsIndex = this.menuItems.findIndex((item) => item.sublink === "/shortcuts");
          this.menuItems[shortcutsIndex].isOpen = true;
        }
        if (route.url.includes("/settings")) {
          let shortcutsIndex = this.menuItems.findIndex((item) => item.sublink === "/settings");
          this.menuItems[shortcutsIndex].isOpen = true;
        }
      })
    );

    config.triggers = "hover";
    config.container = "body";
  }

  get isCompanyUser() {
    const user = this.authService.userDetails.user_details;

    return user?.company_id && user?.status !== "Invited";
  }

  startWalkMeStepTree() {
    this.menuService.openMenu.emit("");
    // this.menuService.openMenu.emit('/flyplates');

    // C3-15: reordered vs. the old Material-menu tour library's array. That library let
    // "Later" (prevBtnTitle) jump to an arbitrary named step via `prevStep:
    // "step11FlyPlates-cancel"` regardless of array order. vng-tour's prev()/
    // next() are strictly sequential (currentIndex -/+ 1), so the "cancel"
    // signal-step is placed BEFORE the real step (index0) and the "ok"
    // signal-step stays AFTER it (index2) — startAt() below jumps straight to
    // the real step, "Later" (prev) sequentially lands on step11FlyPlates-cancel,
    // and "Got it!" (next) sequentially lands on step11FlyPlates-ok, exactly
    // reproducing the old prevStep-redirect behavior.
    this.tourService.initialize([
      {
        anchorId: "step11FlyPlates-cancel",
        content: "",
      },
      {
        anchorId: "step11FlyPlates",
        content: "FlyPlates are templates that you can borrow to use as your FlyCuts.",
        title: "Let’s try adding a FlyPlate",
        enableBackdrop: true,
        disablePageScrolling: true,
        placement: {
          xPosition: "after",
          yPosition: "above",
        },
        // The popover is pushed clear of the sidebar and re-centred on the
        // FlyPlates row, with a caret on its left edge pointing back at it —
        // otherwise it renders on top of the very menu it is describing. Offsets
        // live in app.scss (`.fms-tour-flyplates`), since vng-tour body-portals
        // the popover out of this component's scope.
        popoverClass: "fms-tour-arrow fms-tour-arrow--left fms-tour-flyplates",
        nextBtnTitle: "Got it!",
        prevBtnTitle: "Later",
      },
      {
        anchorId: "step11FlyPlates-ok",
        content: "",
      },
    ]);

    this.tourService.stepShow$.subscribe((step) => {
      if (step.anchorId === "step11FlyPlates") {
        setTimeout(() => tagTourStepButtons("walk-me-modal-add-flyplate-step-11"), 200);
      }

      if (step.anchorId === "step11FlyPlates-ok") {
        this.router.navigateByUrl("/flyplates/viewall");
        this.tourService.end();
      }

      if (step.anchorId === "step11FlyPlates-cancel") {
        this.router.navigateByUrl("/flyplates/viewall");

        this.tourService.end();
        this.globalHookService.onboardingActive = false;
      }
    });

    setTimeout(() => {
      this.tourService.startAt("step11FlyPlates");
    }, 500);
  }

  get isOnboarding() {
    return this.globalHookService.onboardingActive;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.config = {
      ...this.config,
      rtlLayout: this.direction === "rtl" ? true : false,
    };
  }

  ngOnInit() {
    this.subscriptions.add(this.notifierSubscription);

    this.subscriptions.add(
      this.menuService.openMenu.subscribe((item) => {
        this.menuItems.forEach((i, index) => {
          if (i.sublink === item) {
            i.isOpen = true;
            this.selectedMenuIndex = index;
          } else {
            i.isOpen = false;
          }
        });
        setTimeout(() => this.closeSideNav.emit(), 50);
      })
    );

    const updatedMenu = this.menuService.getAll();
    this.menuItems = updatedMenu;

    this.setFlylearning();

    this.subscriptions.add(
      this.eventService.getShortcutCategories().subscribe((data) => {
        this.updateShortcutCategoryList(cloneDeep(data));
      })
    );

    this.setFlyShareList();
    this.getShortcutCategoryList();
    this.getTemplateCategory();

    this.sharedData.storage.observe(SHORTCUT_CATEGORIES).subscribe((value) => {
      this.updateShortcutCategoryList(value);
      this.adminLayoutMenuService._flyCutsSubMenu.next(value);
    });

    this.updateShortcutCategoryList(this.shortcutCategoryList);
    this.adminLayoutMenuService._flyCutsSubMenu.next(this.shortcutCategoryList);
    this.userPlan = this.sharedData.getAttribute("planId")?.toLowerCase();

    this.globalHookService.registerComponent("menu", this);
  }

  ngAfterViewChecked() {
    if (this.selectedMenuIndex !== null) {
      this.scrollToItem(this.selectedMenuIndex);
    }
  }

  private scrollToItem(index: number) {
    const element = document.getElementById("item-" + index);
    if (element) {
      // Ensuring the element is fully rendered before attempting to scroll
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth" });
        this.selectedMenuIndex = null;
      }, 50);
    }
  }

  get invitedToCompany(): boolean {
    return this.user.invited_to_company && this.user.invited_to_company_by_admin;
  }

  goToDashboard() {
    this.router.navigate(["/flyboard"]);
  }

  goToProfile() {
    this.router.navigate(["/settings"], { queryParams: { tab: "Profile" } });
  }

  setFlyShareList() {
    this.submenu.FlyShare = [
      { id: FLYSHARE_ALL_ROUTE, name: "View All" },
      { id: FLYSHARE_SHARED_ROUTE, name: "Shared with Me" },
      { id: FLYSHARE_OTHERS_ROUTE, name: "Shared with Others" },
    ];
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  setQueryParam() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { add: "true" },
      queryParamsHandling: "merge",
    });
  }

  goToAddCategory() {
    if (this.router.url.includes("/settings")) {
      this.eventService.updateAddCategoryListener(true);
    } else {
      this.router.navigate(["/settings"], { queryParams: { add: "true" } });
    }
  }

  goToAddShortcut() {
    if (this.router.url.includes("/shortcuts/")) {
      this.eventService.updateAddShortcutListener(true);
    } else {
      this.router.navigate(["/shortcuts/all"], {
        queryParams: { add: "true" },
      });
    }
  }

  updateShortcutCategoryList(result: any) {
    result = result?.map((item) => {
      item.sub_category_lv1 = item?.sub_category_lv1?.map((sub) => {
        return { ...sub, isSubMenu: true, isOpen: false };
      });
      return { ...item, isSubMenu: true, isOpen: false };
    });
    this.submenu.FlyCuts = result;
    this.submenu.FlyCuts?.unshift(this.viewAll);
  }

  getShortcutCategoryList(reloadFromApi = false) {
    this.shortcutCategoryService.loadShortcutCategory({ reloadFromApi });
  }

  getTemplateCategory() {
    this.dataService.get({ url: "v1/user/template-category", isLoader: false }).subscribe((response: any) => {
      if (response && response.result) {
        const result = response.result.map((item) => {
          item.subcategory_lv1 = item?.subcategory_lv1?.map((sub) => {
            return { ...sub, isSubMenu: true, isOpen: false };
          });
          return { ...item, isSubMenu: true, isOpen: false };
        });

        this.submenu.FlyPlates = result;
        this.submenu.FlyPlates.unshift(this.viewAll);
        this.adminLayoutMenuService._flyPlatesSubMenu.next(response.result);
      }
    });
  }

  getFlyPlateCategorySlug(category: { id?: string; name?: string; slug?: string }): string {
    if (category.id === "all") {
      return "viewall";
    }

    return category.slug || (category.name || "").replace(/\s+/g, "").toLowerCase();
  }

  updateShortcutCategoryPosition(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.submenu.FlyCuts, event.previousIndex, event.currentIndex);

    const data = {
      positions: [],
      // type:"categorylv1"   // changing a position of categorylv1
      //  type:"categorylv1"  //changing a position of categorylv2
    };

    this.submenu.FlyCuts.forEach((shortcut: any) => {
      if (shortcut.id !== "all") {
        data.positions.push(shortcut.id);
      }
    });

    this.dataService
      .put({ url: "v1/user/shortcut-category-position", data, isLoader: false })
      .subscribe((response: any) => {
        if (response && response.result) {
          this.getShortcutCategoryList(true);
        }
      });
  }

  navigateTo(site: string) {
    this.navigationService.navigateToUrl(site);
  }

  openRolePlay(item) {
    if (item.sso && item.sso == "roleplay") {
      this.ssoRedirect.openRolePlay();
    }
  }

  openlearning(item) {
    if (item.sso && item.sso == "flylearning") {
      this.ssoRedirect.openFlyLearning();
    }
  }

  setFlylearning() {
    // FlyLearning visibility is now managed via NgRx store subscription in constructor
  }

  handleScrollBar() {
    this.menuHeight.nativeElement.style.overflowY = "hidden";
    setTimeout(() => {
      this.menuHeight.nativeElement.style.overflowY = "scroll";
    }, 1000);
  }

  collapseSideNav(item) {
    if (item.label.toLowerCase() === "flyboard") {
      this.menuService.closeSideNavAfterNavigation.next(true);
    }
  }

  handleCloseSideNav(item) {
    this.menuService.closeSideNavAfterNavigation.next(false);
  }

  handleAccountOpening(url: string | URL) {
    window.open(url, "_blank");
  }
}
